
import { motion, useTransform, type MotionValue } from "framer-motion";
import { FLOATING_ICONS, PERIPHERAL_NODES } from "../keyframe-data";
import type { KeyframeBand } from "../types";
import { NodeCard, type ServiceIconId } from "../elements/node-card";
import { PeripheralCard } from "../elements/peripheral-card";
import { NODE_CARD } from "../graph-layout";

interface Props {
  progress: MotionValue<number>;
  /** This beat's band (beat 1). */
  band: KeyframeBand;
  /** Beat 2 — peripherals/floats hold full opacity through its start. */
  nextBand: KeyframeBand;
  /** Beat 3 — peripherals/floats fully gone by here as the graph settles. */
  bandAfterNext: KeyframeBand;
}

const PERIPHERAL_OPACITY = 0.65;

export function Beat1Unmapped({
  progress,
  band,
  nextBand,
  bandAfterNext,
}: Props) {
  // Focused workload nodes hold through beat 2; fade as beat 3 begins.
  const opacity = useTransform(
    progress,
    [band.start, nextBand.start, bandAfterNext.start],
    [1, 1, 0],
  );
  // Peripherals fade a touch earlier so the focused 6 take the foreground first.
  const peripheralOpacity = useTransform(
    progress,
    [band.start, nextBand.start, bandAfterNext.start - 0.02],
    [PERIPHERAL_OPACITY, PERIPHERAL_OPACITY, 0],
  );

  return (
    <g data-beat="1" aria-hidden="true">
      <motion.g style={{ opacity: peripheralOpacity }}>
        {PERIPHERAL_NODES.map((p) => (
          <PeripheralCard key={p.id} id={p.id} label={p.label} x={p.x} y={p.y} />
        ))}
      </motion.g>
      <motion.g style={{ opacity }}>
        {FLOATING_ICONS.map((icon) => (
          <NodeCard
            key={icon.id}
            id={icon.id as ServiceIconId}
            x={icon.x - NODE_CARD.width / 2}
            y={icon.y - NODE_CARD.height / 2}
            hideResourceId
          />
        ))}
      </motion.g>
    </g>
  );
}
